import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// FO-SOI-4 master-detail Sales Order workspace state.
//
// This store owns LIST-LEVEL navigation (selected row, master pane width,
// search input, status tab). The existing `salesOrderStore` keeps owning
// ACTIVE-DRAFT lifecycle (activeDraft, dirty, lastSavedAt, save/submit). The
// detail panel reaches into it via `loadDraft` when a row is clicked, so
// dirty/save mechanics behave exactly as they do on the legacy compose page.
//
// Coexists with the legacy /sales-orders list and /sales-orders/compose/:id
// flow during rollout — the workspace is opt-in for whoever lands on
// /sales-orders/workspace.

export type FilterTab = 'drafts' | 'submitted' | 'archived';

// Fixed pixel range mirrors quotation — list reads better at a tight,
// predictable width regardless of viewport; detail pane absorbs the rest.
const MASTER_WIDTH_MIN_PX = 290;
const MASTER_WIDTH_MAX_PX = 330;
const MASTER_WIDTH_DEFAULT_PX = 310;

// Mirrors the Quotation workspace pin: up to 5 records pinned for quick return,
// FIFO-evict the oldest on the 6th so the chip strip stays single-row.
const PIN_CAP = 5;

interface SalesOrderWorkspaceState {
  selectedId: number | null;
  statusFilter: FilterTab;
  quickFilter: string;
  masterPanelWidthPx: number;
  /** Server ids the user has pinned for fast return via the chip strip.
   *  FIFO-evict on the 6th pin so the strip can't grow unbounded. */
  pinnedIds: number[];

  setSelectedId: (id: number | null) => void;
  setStatusFilter: (f: FilterTab) => void;
  setQuickFilter: (q: string) => void;
  setMasterPanelWidthPx: (px: number) => void;
  togglePin: (id: number) => void;
  isPinned: (id: number) => boolean;
}

export const useSalesOrderWorkspaceStore = create<SalesOrderWorkspaceState>()(
  persist(
    (set, get) => ({
      selectedId: null,
      statusFilter: 'drafts',
      quickFilter: '',
      masterPanelWidthPx: MASTER_WIDTH_DEFAULT_PX,
      pinnedIds: [],

      setSelectedId: (id) => set({ selectedId: id }),
      setStatusFilter: (f) => set({ statusFilter: f }),
      setQuickFilter: (q) => set({ quickFilter: q }),
      setMasterPanelWidthPx: (px) => {
        const clamped = Math.min(MASTER_WIDTH_MAX_PX, Math.max(MASTER_WIDTH_MIN_PX, px));
        set({ masterPanelWidthPx: clamped });
      },
      togglePin: (id) => {
        const current = get().pinnedIds;
        if (current.includes(id)) {
          set({ pinnedIds: current.filter((x) => x !== id) });
          return;
        }
        // FIFO-evict the oldest pin at the cap so the strip stays single-row.
        const next = current.length >= PIN_CAP ? [...current.slice(1), id] : [...current, id];
        set({ pinnedIds: next });
      },
      isPinned: (id) => get().pinnedIds.includes(id),
    }),
    {
      name: 'sales-order-workspace',
      storage: createJSONStorage(() => sessionStorage),
      // selectedId lives in the URL (?id=) — rehydrating from session would
      // race the URL parser. quickFilter/statusFilter/pinnedIds are persisted so
      // the user's last view + pins stick across reload.
      partialize: (state) => ({
        statusFilter: state.statusFilter,
        quickFilter: state.quickFilter,
        masterPanelWidthPx: state.masterPanelWidthPx,
        pinnedIds: state.pinnedIds,
      }),
    },
  ),
);
